import axios from 'ts-axios-new';
import {isRetryableBatchImageRequest, shouldDriveBatchImageJob} from './batchImagePolicy';

const STORAGE_PREFIX = 'jigsaw_batch_image_job_id';
const runningJobs = new Set<string>();
const pausedJobs = new Set<string>();

const terminal = new Set(['finished', 'finished_with_errors', 'failed', 'cancelled']);
const maxConsecutiveFailures = 10;

function emit(job: any, product: string) {
  window.dispatchEvent(new CustomEvent('batch-image-job-update', {detail: {job, product}}));
}

function storageKey(product: string) {
  return `${STORAGE_PREFIX}:${product}`;
}

function delay(milliseconds: number) {
  return new Promise(resolve => window.setTimeout(resolve, milliseconds));
}

async function fetchBatchImageJob(jobId: string, product: string) {
  const response = await axios.get(`/cms/v1/batch-image/job/${jobId}`, {headers: {product}});
  return response.data?.data?.job || null;
}

async function fetchBatchImageJobSafely(jobId: string, product: string) {
  try {
    return await fetchBatchImageJob(jobId, product);
  } catch (_) {
    return null;
  }
}

export async function runBatchImageJob(jobId: string, product: string) {
  const runnerKey = `${product}:${jobId}`;
  if (runningJobs.has(runnerKey) || pausedJobs.has(runnerKey)) return;
  runningJobs.add(runnerKey);
  let consecutiveRequestFailures = 0;
  try {
    const initialJob = await fetchBatchImageJobSafely(jobId, product);
    if (initialJob) {
      emit(initialJob, product);
      if (terminal.has(initialJob.status)) {
        localStorage.removeItem(storageKey(product));
        return;
      }
      // An uploading job can only be completed by the page that still owns
      // the local File object. Never race its chunk loop after a refresh.
      const completedUpload = initialJob.status === 'uploading'
        && Number(initialJob.received_chunks || 0) >= Number(initialJob.total_chunks || 0)
        && Number(initialJob.total_chunks || 0) > 0;
      if (!completedUpload && !shouldDriveBatchImageJob(initialJob.status)) return;
    }
    while (true) {
      try {
        const response = await axios.post(`/cms/v1/batch-image/job/${jobId}/process`, {}, {
          headers: {product},
        });
        const job = response.data.data.job;
        emit(job, product);
        consecutiveRequestFailures = 0;
        if (terminal.has(job.status)) {
          localStorage.removeItem(storageKey(product));
          return;
        }
        await delay(300);
      } catch (error: any) {
        consecutiveRequestFailures += 1;
        const status = error?.response?.status;
        const message = error?.response?.data?.status?.message
          || error?.response?.data?.message
          || error?.message
          || '网络连接中断或网关未响应';
        const detail = status ? `HTTP ${status}：${message}` : message;
        const delayMs = Math.min(30000, 3000 * (2 ** Math.min(consecutiveRequestFailures - 1, 3)));
        const serverJob = await fetchBatchImageJobSafely(jobId, product);
        if (serverJob && terminal.has(serverJob.status)) {
          emit(serverJob, product);
          localStorage.removeItem(storageKey(product));
          return;
        }
        const retryable = isRetryableBatchImageRequest(status);
        if (!retryable || consecutiveRequestFailures >= maxConsecutiveFailures) {
          if (status === 404) localStorage.removeItem(storageKey(product));
          pausedJobs.add(runnerKey);
          emit({
            ...serverJob,
            id: jobId,
            status: serverJob?.status || 'ready',
            client_paused: true,
            status_message: !retryable
              ? `后台任务请求被拒绝，已停止自动重试：${detail}`
              : `后台连续请求失败 ${consecutiveRequestFailures} 次，已暂停自动重试。请刷新页面恢复，或取消任务：${detail}`,
          }, product);
          return;
        }
        const serverStage = serverJob?.status_message
          ? `；后端当前阶段：${serverJob.status_message}`
          : '；未读取到后端任务状态';
        emit({
          ...serverJob,
          id: jobId,
          status: serverJob?.status || 'merging',
          status_message: `后台任务请求失败（第 ${consecutiveRequestFailures} 次），${delayMs / 1000} 秒后自动重试：${detail}${serverStage}`,
        }, product);
        await delay(delayMs);
      }
    }
  } finally {
    runningJobs.delete(runnerKey);
  }
}

export function rememberAndRunBatchImageJob(jobId: string, product: string) {
  localStorage.setItem(storageKey(product), jobId);
  void runBatchImageJob(jobId, product);
}

export function retryBatchImageJob(jobId: string, product: string) {
  pausedJobs.delete(`${product}:${jobId}`);
  rememberAndRunBatchImageJob(jobId, product);
}

export function resumeBatchImageJobs() {
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index) || '';
    if (!key.startsWith(`${STORAGE_PREFIX}:`)) continue;
    const jobId = localStorage.getItem(key);
    const product = key.slice(`${STORAGE_PREFIX}:`.length);
    if (jobId && product) void runBatchImageJob(jobId, product);
  }
}
